import { useCallback, useMemo } from "react";
import { useFetcher } from "~/hooks/useFetcher";
import { useUserPreferences, type ConnectorPrefsEntry } from "~/hooks/useUserPreferences";
import { agentConnectorsPath, type AgentConnector } from "~/services/agents";
import { trackEvent } from "~/utils/monitoring";

/**
 * Resolves which connector names should be selected given the agent's
 * granted set and any saved preferences for this context.
 *
 * - Saved selections survive only when the name is still in the granted set.
 * - Newly-granted names (not previously selected) default to OFF — MCPs are
 *   off by default; the user opts in explicitly.
 * - Previously-granted names respect the user's saved choice.
 */
export function resolveSelectedConnectors(
  granted: AgentConnector[],
  saved: ConnectorPrefsEntry
): string[] {
  const grantedNames = new Set(granted.map((g) => g.connector.name));
  return saved.selected.filter((name) => grantedNames.has(name));
}

interface UseAgentConnectorsResult {
  /** Granted connectors for the agent. Empty array while loading or for legacy
   * conversations that haven't surfaced an agent_id yet. */
  agentConnectors: AgentConnector[];
  /** Connector names selected for the current context (subset of granted). */
  selectedConnectorNames: string[];
  /** Toggle a single connector by name; persists the result to user preferences. */
  toggleConnector: (connectorName: string) => void;
  isLoading: boolean;
}

/**
 * Fetches `GET /api/conversations/v1/agents/{agentId}/connectors` and
 * manages a per-context selection of connector NAMES. The selection is
 * persisted in localStorage so it survives a page refresh.
 *
 * The selection — emitted as `forwardedProps.connectors` by
 * `useForwardConnectors` in the Composer — is the per-turn allow-list
 * sent to the analysis-agent.
 */
export function useAgentConnectors(
  agentId: string | null,
  contextId: string
): UseAgentConnectorsResult {
  const fetchKey = agentId ? agentConnectorsPath(agentId) : null;
  const { data, isLoading } = useFetcher<AgentConnector[]>(fetchKey);
  const agentConnectors = useMemo(() => data ?? [], [data]);

  const [preferences, updatePreferences] = useUserPreferences();

  const selectedConnectorNames = useMemo(() => {
    if (agentConnectors.length === 0) {
      return [];
    }
    const saved = contextId ? (preferences.connectorPreferences?.[contextId] ?? null) : null;
    if (saved) {
      return resolveSelectedConnectors(agentConnectors, saved);
    }
    // Fresh context: connectors are off by default — the user opts in per turn.
    return [];
  }, [agentConnectors, preferences.connectorPreferences, contextId]);

  const toggleConnector = useCallback(
    (connectorName: string) => {
      const isCurrentlySelected = selectedConnectorNames.includes(connectorName);
      const newSelection = isCurrentlySelected
        ? selectedConnectorNames.filter((n) => n !== connectorName)
        : [...selectedConnectorNames, connectorName];

      trackEvent("assistant_v2_connector_toggled", {
        dataset_id: contextId || "Unknown",
        context: "dataset",
        connector_name: connectorName,
        enabled: !isCurrentlySelected,
        total_selected: newSelection.length,
      });

      if (contextId) {
        const availableNames = agentConnectors.map((ac) => ac.connector.name);
        updatePreferences({
          connectorPreferences: {
            ...preferences.connectorPreferences,
            [contextId]: { selected: newSelection, available: availableNames },
          },
        });
      }
    },
    [
      selectedConnectorNames,
      agentConnectors,
      contextId,
      preferences.connectorPreferences,
      updatePreferences,
    ]
  );

  return {
    agentConnectors,
    selectedConnectorNames,
    toggleConnector,
    isLoading,
  };
}
